/**
 * Hero banner slider — horizontal slide (desktop + mobile)
 */
(() => {
  class HeroSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      this.slides = [...root.querySelectorAll('[data-slide]')];
      this.dots = [...root.querySelectorAll('[data-slider-dot]')];
      this.prevBtn = root.querySelector('[data-slider-prev]');
      this.nextBtn = root.querySelector('[data-slider-next]');
      this.index = 0;
      this.autoplayMs = Number(root.dataset.autoplay) || 5000;
      this.timer = null;
      this.startX = 0;
      this.deltaX = 0;
      this.dragging = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track || this.slides.length <= 1) {
        this.root.classList.add('is-single');
        return;
      }

      this.root.classList.add('hero-slider--track');
      this.bind();
      this.goTo(0, false);
      this.start();
    }

    bind() {
      this.prevBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(this.index - 1);
      });
      this.nextBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(this.index + 1);
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(i);
        });
      });

      const controls = this.root.querySelector('.hero-slider__controls');
      controls?.addEventListener('mouseenter', () => this.stop());
      controls?.addEventListener('mouseleave', () => this.start());

      const stage = this.root.querySelector('.hero-slider__viewport') || this.root;

      stage.addEventListener(
        'pointerdown',
        (e) => {
          if (e.target.closest('a, button')) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          this.dragging = true;
          this.startX = e.clientX;
          this.deltaX = 0;
          this.stop();
          this.track.style.transition = 'none';
          try {
            stage.setPointerCapture(e.pointerId);
          } catch (_) {}
        },
        { passive: true }
      );

      stage.addEventListener(
        'pointermove',
        (e) => {
          if (!this.dragging) return;
          this.deltaX = e.clientX - this.startX;
          const pct = (this.deltaX / stage.clientWidth) * 100;
          this.track.style.transform = `translate3d(${-this.index * 100 + pct}%, 0, 0)`;
        },
        { passive: true }
      );

      const endDrag = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.style.transition = '';
        if (Math.abs(this.deltaX) > 60) {
          this.goTo(this.deltaX < 0 ? this.index + 1 : this.index - 1);
        } else {
          this.goTo(this.index);
        }
      };

      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);
      window.addEventListener('resize', () => this.goTo(this.index, false));
    }

    goTo(i, animate = true) {
      const len = this.slides.length;
      this.index = ((i % len) + len) % len;

      if (!animate || this.reduced) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = '';
      }
      this.track.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;

      this.slides.forEach((slide, idx) => {
        const active = idx === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      this.dots.forEach((dot, idx) => {
        const on = idx === this.index;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.stop();
      this.start();
    }

    start() {
      if (this.reduced || this.autoplayMs <= 0 || this.slides.length <= 1) return;
      this.stop();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.autoplayMs);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  }

  function init() {
    document.querySelectorAll('[data-hero-slider]').forEach((el) => {
      if (el.dataset.sliderReady) return;
      el.dataset.sliderReady = '1';
      new HeroSlider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
